import { Box, MenuItem, Select, Typography, type SelectChangeEvent } from '@mui/material'
import { ChevronDownIcon } from '@untitled-theme/icons-react'
import { useState } from 'react'
import { useParams } from 'react-router-dom'

import { useISFPData } from '@/shared/api'
import HamburgerMenu from '@/shared/components/HamburgerMenu'
import LoadingIndicator from '@/shared/components/LoadingIndicator'
import { useAssessment } from '@/shared/hooks/useAssessment'
import { useCustomPackages } from '@/shared/hooks/useCustomPackages'
import { getRetrofitDisplayName } from '@/shared/utils/displayMappings'

import BAFAMetricsDisplay from '../components/BAFAMetricsDisplay'

const BAFADetailsPage = () => {
  const { buildingUuid } = useParams<{ buildingUuid: string }>()
  const { assessment, loading: assessmentLoading } = useAssessment(buildingUuid)
  const { data: isfpData, isLoading: isfpLoading } = useISFPData(buildingUuid)
  const { customPackages, loading: packagesLoading } = useCustomPackages(buildingUuid)
  const [selectedValue, setSelectedValue] = useState<string>('')

  const loading = assessmentLoading || isfpLoading || packagesLoading

  const retrofitStates = assessment?.data?.demandBasedAssessment?.retrofitStates ?? []
  const processedPackages = customPackages.filter(pkg => pkg.status === 'processed')
  const currentFinalEnergy =
    assessment?.data?.demandBasedAssessment?.currentState?.energetics?.annualFinalEnergyInKwh
      ?.total ?? 0
  const usableSpace = isfpData?.buildingInformation?.usableSpaceInSqm ?? 0
  const currentTransmissionHeatLoss = isfpData?.energeticValues?.transmissionHeatLossInWPerK
  const currentPrimaryEnergy = isfpData?.energeticValues?.primaryEnergyDemandInKwh
  const currentCo2Emissions = isfpData?.energeticValues?.equivalentCo2EmissionsInTons

  const selectedEnergetics = (() => {
    if (selectedValue.startsWith('retrofit-')) {
      const index = Number(selectedValue.replace('retrofit-', ''))
      return retrofitStates[index]?.energetics ?? null
    }
    if (selectedValue.startsWith('package-')) {
      const index = Number(selectedValue.replace('package-', ''))
      const pkg = processedPackages[index]
      return pkg?.data.demandBasedCalculation.energetics ?? null
    }
    return null
  })()

  const handleSelectionChange = (event: SelectChangeEvent<string>) => {
    setSelectedValue(event.target.value)
  }

  return (
    <Box
      sx={{
        position: 'relative',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'flex-start',
        minHeight: '100vh',
        width: '100vw',
        backgroundColor: '#ffffff',
        padding: { xs: '0 24px' },
      }}
    >
      <HamburgerMenu
        sx={{
          position: 'fixed',
          top: 32,
          left: 32,
          display: { xs: 'flex', lg: 'none' },
        }}
      />

      <Box
        sx={{
          maxWidth: { xs: '100%', lg: '1125px' },
          width: '100%',
          paddingBottom: { xs: '180px', lg: '60px' },
        }}
      >
        <Box
          sx={{
            position: 'sticky',
            top: 0,
            background: 'linear-gradient(180deg, #ffffff 0%, #ffffff 90%, transparent 100%)',
            zIndex: 100,
            paddingTop: { xs: 10, lg: 5 },
            paddingBottom: 2,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              marginBottom: 3,
            }}
          >
            <Box sx={{ display: { xs: 'none', lg: 'flex' } }}>
              <HamburgerMenu />
            </Box>
            <Typography
              component="h1"
              sx={{
                fontWeight: 600,
                fontSize: '28px',
                lineHeight: '42px',
                marginLeft: '8px',
              }}
            >
              <Box component="span" sx={{ color: '#5BC06B' }}>
                BAFA
              </Box>{' '}
              <Box component="span">Angaben</Box>
            </Typography>
          </Box>
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', paddingTop: 8 }}>
            <LoadingIndicator />
          </Box>
        ) : (
          <Box sx={{ maxWidth: { xs: '100%', lg: '600px' } }}>
            <Select
              value={selectedValue}
              onChange={handleSelectionChange}
              displayEmpty
              fullWidth
              IconComponent={ChevronDownIcon}
              sx={{
                marginBottom: 3,
                backgroundColor: '#F3F3F6',
                borderRadius: '16px',
                fontWeight: 500,
                fontSize: '15px',
                color: selectedValue === '' ? '#9E9E9E' : '#181D29',
                '& .MuiOutlinedInput-notchedOutline': {
                  border: 'none',
                },
                '& .MuiSelect-select': {
                  padding: '14px 20px',
                },
                '& .MuiSelect-icon': {
                  right: '16px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  width: '20px',
                  height: '20px',
                },
                '& .MuiSelect-iconOpen': {
                  transform: 'translateY(-50%) rotate(180deg)',
                },
              }}
              MenuProps={{
                PaperProps: {
                  sx: {
                    backgroundColor: '#F3F3F6',
                    borderRadius: '16px',
                    boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.08)',
                    marginTop: '4px',
                  },
                },
              }}
            >
              <MenuItem value="" disabled sx={{ color: '#9E9E9E', fontWeight: 500 }}>
                Sanierungsmaßnahme auswählen
              </MenuItem>
              {retrofitStates.map((retrofit, index) => (
                <MenuItem
                  key={`retrofit-${retrofit.retrofitName}-${index}`}
                  value={`retrofit-${index}`}
                  sx={{
                    fontWeight: 500,
                    fontSize: '15px',
                    color: '#181D29',
                    padding: '14px 20px',
                    '&:hover': { backgroundColor: '#EAEAED' },
                  }}
                >
                  {getRetrofitDisplayName(retrofit.retrofitName)}
                </MenuItem>
              ))}
              {processedPackages.map((pkg, index) => (
                <MenuItem
                  key={`package-${pkg.id}`}
                  value={`package-${index}`}
                  sx={{
                    fontWeight: 500,
                    fontSize: '15px',
                    color: '#181D29',
                    padding: '14px 20px',
                    '&:hover': { backgroundColor: '#EAEAED' },
                  }}
                >
                  {`Paket ${String(index + 1).padStart(2, '0')} – ${pkg.measures.map(getRetrofitDisplayName).join(', ')}`}
                </MenuItem>
              ))}
            </Select>

            {selectedEnergetics && (
              <BAFAMetricsDisplay
                usableSpaceInSqm={usableSpace}
                currentTransmissionHeatLossInWPerK={currentTransmissionHeatLoss}
                retrofitTransmissionHeatLossInWPerK={selectedEnergetics.transmissionHeatLossInWPerK}
                currentFinalEnergyInKwh={currentFinalEnergy}
                retrofitFinalEnergyInKwh={selectedEnergetics.annualFinalEnergyInKwh.total}
                currentPrimaryEnergyInKwh={currentPrimaryEnergy}
                retrofitPrimaryEnergyInKwh={selectedEnergetics.annualPrimaryEnergyInKwh?.total}
                currentCo2EmissionsInTons={currentCo2Emissions}
                retrofitCo2EmissionsInTons={selectedEnergetics.annualCo2EmissionsInTons}
              />
            )}
          </Box>
        )}
      </Box>
    </Box>
  )
}

export default BAFADetailsPage
